// A week's games as tiles (B-385): the scoreboard's, and the schedule's
// week (B-386). One team's game first — mine, or the team whose season is on
// screen — the rest in the order the league serves them, which does not move
// as the scores do: a game found once is where it was. In a week of the
// bracket its games and the consolation games each have a heading.

import type { AwardCardView, MatchupSummary, StatsFreshnessShape } from '@fsp/domain';
import { EmptyState } from '../../kit';
import { GameTile } from './GameTile';
import '../../styles/screens/scoreboard.css';

export function GameGrid({
  games,
  awards,
  stats,
  big = false,
  first,
}: {
  games: readonly MatchupSummary[];
  awards?: AwardCardView[];
  stats: StatsFreshnessShape;
  /** A large screen: bigger type, a board to read from across a room (DESIGN.md §4). */
  big?: boolean;
  /** The team whose game leads and reads on top; mine when not given. */
  first?: string;
}) {
  const leads = (m: MatchupSummary) =>
    first === undefined
      ? m.home.team.isMine || m.away.team.isMine
      : m.home.team.id === first || m.away.team.id === first;
  const ordered = [...games.filter(leads), ...games.filter((m) => !leads(m))];
  const bracket = ordered.filter((m) => m.kind === 'playoff');
  const consolation = ordered.filter((m) => m.kind === 'consolation');
  const groups =
    bracket.length + consolation.length === 0
      ? [{ title: null, games: ordered }]
      : [
          { title: 'Playoffs', games: bracket },
          { title: 'Consolation', games: consolation },
          {
            title: 'Other games',
            games: ordered.filter((m) => m.kind !== 'playoff' && m.kind !== 'consolation'),
          },
        ]
          .filter((g) => g.games.length > 0)
          // THE LEADING GAME FIRST IN A PLAYOFF WEEK TOO: its group leads, so
          // a team in the consolation games does not find the bracket's
          // games above its own.
          .sort((a, b) => Number(b.games.some(leads)) - Number(a.games.some(leads)));
  if (games.length === 0) {
    // Nothing under the title read as a page that had not loaded
    // (AUDIT-2026-09-23 U9).
    return <EmptyState>No games this week.</EmptyState>;
  }
  return (
    <div className="fsp-sb-games">
      {groups.map((g) => (
        <section
          key={g.title ?? 'games'}
          className="fsp-sb-group"
          aria-label={g.title ?? undefined}
        >
          {g.title !== null ? <h3 className="fsp-cap fsp-sb-group-title">{g.title}</h3> : null}
          {/* NO MORE COLUMNS THAN THE GAMES FILL (`columnsFor`): four games on
              a wide screen were one thin row over an empty page, where two
              rows of two are a board. */}
          <div className="fsp-sb-grid" data-max={columnsFor(g.games.length)}>
            {g.games.map((m) => (
              <GameTile key={m.id} m={m} awards={awards} stats={stats} big={big} top={first} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * The most columns a group of games takes, however wide the screen: up to
 * four games in two (a square of two by two), five or six in three, more in
 * four. A narrower column than these fit takes fewer (scoreboard.css).
 */
export function columnsFor(games: number): string {
  return games <= 1 ? '1' : games <= 4 ? '2' : games <= 6 ? '3' : '4';
}
